import type { MutationFunction } from '@apollo/client';

import type {
  CreateAdsPageAdMutation,
  CreateAdsPageAdMutationVariables,
  CreateAdsPageAdWithAssetMutation,
  CreateAdsPageAdWithAssetMutationVariables,
  CreateAdWithBatchAssetsMutation,
  CreateAdWithBatchAssetsMutationVariables,
  CreateAdWithGatewayAssetsMutation,
  CreateAdWithGatewayAssetsMutationVariables,
} from '../../../../../apis/graphql';
import type { AdFormValues } from '../../../../../common/AdForm/adFormik';
import type { AssetCountryCodesInput } from '../../util/buildAssetCountryCodesInput';
import type { GatewayGoAssetCountryCodesInput } from '../../util/buildGatewayAssetCountryCodesInput';
import { constructCreateAdInput } from './constructCreateAdInput';
import { getAssetId } from './getAssetId';

type Mutations = {
  createAd: MutationFunction<CreateAdsPageAdMutation>;
  createAdWithAsset: MutationFunction<CreateAdsPageAdWithAssetMutation>;
  createAdWithBatchAssets: MutationFunction<CreateAdWithBatchAssetsMutation>;
  createAdWithGatewayAssets: MutationFunction<CreateAdWithGatewayAssetsMutation>;
};

type WithoutAsset = { mutation: Mutations['createAd']; variables: CreateAdsPageAdMutationVariables };
type WithAsset = { mutation: Mutations['createAdWithAsset']; variables: CreateAdsPageAdWithAssetMutationVariables };
type WithBatchAssets = {
  mutation: Mutations['createAdWithBatchAssets'];
  variables: CreateAdWithBatchAssetsMutationVariables;
};
type WithGatewayAssets = {
  mutation: Mutations['createAdWithGatewayAssets'];
  variables: CreateAdWithGatewayAssetsMutationVariables;
};

export function getCreateMutation(
  mutations: Mutations,
  adFormValues: AdFormValues,
  lineItemId: string,
  assetCountryCodesInput?: AssetCountryCodesInput | GatewayGoAssetCountryCodesInput
): WithoutAsset | WithAsset | WithBatchAssets | WithGatewayAssets {
  const assetId = getAssetId(adFormValues.creative.type, adFormValues);
  const creativeLibraryId = adFormValues.creative.creativeLibraryId ?? '';

  // The Ad has a GatewayGo Asset
  if (
    (assetCountryCodesInput as GatewayGoAssetCountryCodesInput)?.displayCountryCodes &&
    (assetCountryCodesInput as GatewayGoAssetCountryCodesInput)?.videoCountryCodes
  ) {
    const withGatewayAssets: WithGatewayAssets = {
      mutation: mutations.createAdWithGatewayAssets,
      variables: {
        adInput: constructCreateAdInput(adFormValues, lineItemId),
        displayAssetId: adFormValues.creative.image?.id || '',
        displayAssetCountryCodes: (assetCountryCodesInput as GatewayGoAssetCountryCodesInput).displayCountryCodes,
        videoAssetId: adFormValues.creative.video?.id || '',
        videoAssetCountryCodes: (assetCountryCodesInput as GatewayGoAssetCountryCodesInput).videoCountryCodes,
        creativeLibraryId,
      },
    };
    return withGatewayAssets;
  }

  // The Ad has one or more standard Assets for mutation
  if (assetId && assetCountryCodesInput) {
    // The Ad hsa a single Asset
    if (typeof assetId === 'string') {
      const withAsset: WithAsset = {
        mutation: mutations.createAdWithAsset,
        variables: {
          adInput: constructCreateAdInput(adFormValues, lineItemId),
          assetId,
          countryCodes: (assetCountryCodesInput as AssetCountryCodesInput).countryCodes,
          creativeLibraryId,
        },
      };
      return withAsset;
    }

    // The Ad has multiple Assets
    if (Array.isArray(assetId)) {
      const withBatchAssets: WithBatchAssets = {
        mutation: mutations.createAdWithBatchAssets,
        variables: {
          adInput: constructCreateAdInput(adFormValues, lineItemId),
          idList: assetId,
          countryCodes: (assetCountryCodesInput as AssetCountryCodesInput).countryCodes,
          creativeLibraryId,
        },
      };
      return withBatchAssets;
    }
  }

  // No Asset condition has been met; we're creating the Ad only
  const withoutAsset: WithoutAsset = {
    mutation: mutations.createAd,
    variables: { adInput: constructCreateAdInput(adFormValues, lineItemId) },
  };
  return withoutAsset;
}
